// The announcement icon library (docs/ANNOUNCEMENTS.md): 24 pastel pictures in
// the house art style (worker/components/art/WorkerArt.jsx, the Hub's HubArt):
// flat, rounded, no outlines, each object lifted off the page by a `depth`
// layer offset down and right. 72 by 72, decorative (the card carries the words).
//
// ⛔ Colours are {{slot}}s filled from shared/pastelTones.js only: never
// turquoise (the logo) or the link blue (actions).
//
// ⛔ NO IMPORTS, on purpose. servers/onboarding/src/lib/announcementIcons.js is a
// BYTE FOR BYTE copy (the admin console's picker draws what the api sends it,
// because the console's Docker context cannot reach shared/), and
// test/announcements.test.mjs fails if the two drift. Edit here, then copy.
//
// Render: fillIcon(ANNOUNCEMENT_ICON_XML[key], PASTEL_TONES[scheme]) → an SVG
// string for react-native-svg's SvgXml (apps) or innerHTML (console).

const lift = (shape, fill, dx = 3, dy = 3) => `<g transform="translate(${dx} ${dy})">${shape('{{depth}}')}</g>${shape(fill)}`

// A filled polygon with its corners rounded by a same colour stroke.
const soft = (d, w = 4) => (fill) => `<path d="${d}" fill="${fill}" stroke="${fill}" stroke-width="${w}" stroke-linejoin="round"/>`
const path = (d) => (fill) => `<path d="${d}" fill="${fill}"/>`
const line = (d, stroke, w = 3) => `<path d="${d}" fill="none" stroke="${stroke}" stroke-width="${w}" stroke-linecap="round" stroke-linejoin="round"/>`
const dot = (x, y, r, fill) => `<circle cx="${x}" cy="${y}" r="${r}" fill="${fill}"/>`
const rect = (x, y, w, h, r, fill, extra = '') => `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="${r}" fill="${fill}"${extra}/>`

// The four point sparkle used across our art.
function sparkle(x, y, r, fill) {
  const k = r * 0.18
  return `<path d="M${x} ${y - r}Q${x + k} ${y - k} ${x + r} ${y}Q${x + k} ${y + k} ${x} ${y + r}Q${x - k} ${y + k} ${x - r} ${y}Q${x - k} ${y - k} ${x} ${y - r}Z" fill="${fill}"/>`
}

// A heart centred on (x, y), s = half width.
const heartD = (x, y, s) => `M${x} ${y + s * 0.95}C${x - s * 1.5} ${y - s * 0.05} ${x - s * 0.95} ${y - s * 1.25} ${x} ${y - s * 0.45}C${x + s * 0.95} ${y - s * 1.25} ${x + s * 1.5} ${y - s * 0.05} ${x} ${y + s * 0.95}Z`

// A five point star centred on (x, y).
function starD(x, y, R, r = R * 0.48) {
  let d = ''
  for (let i = 0; i < 10; i++) {
    const a = -Math.PI / 2 + (i * Math.PI) / 5
    const rr = i % 2 ? r : R
    d += `${i ? 'L' : 'M'}${(x + rr * Math.cos(a)).toFixed(1)} ${(y + rr * Math.sin(a)).toFixed(1)}`
  }
  return `${d}Z`
}

// A calendar page: body, header band, two rings.
const calendar = (body, head) => `${lift(path('M18 20h36a6 6 0 0 1 6 6v26a6 6 0 0 1-6 6H18a6 6 0 0 1-6-6V26a6 6 0 0 1 6-6z'), body)}`
  + `<path d="M18 20h36a6 6 0 0 1 6 6v6H12v-6a6 6 0 0 1 6-6z" fill="${head}"/>`
  + rect(24, 14, 4, 11, 2, '{{depth}}') + rect(44, 14, 4, 11, 2, '{{depth}}')

// A gear: eight teeth round a disc.
const gear = (cx, cy, R, fill) => {
  let teeth = ''
  for (let i = 0; i < 8; i++) teeth += `<rect x="${cx - R * 0.22}" y="${cy - R * 1.32}" width="${R * 0.44}" height="${R * 0.6}" rx="${R * 0.12}" fill="${fill}" transform="rotate(${i * 45} ${cx} ${cy})"/>`
  return `${teeth}<circle cx="${cx}" cy="${cy}" r="${R}" fill="${fill}"/>`
}

const ICONS = {
  // A green tree in three tiers, a yellow star, baubles.
  christmas: lift(soft('M36 10L52 32H45L57 50H15L27 32H20Z'), '{{green}}')
    + rect(32, 52, 8, 9, 2, '{{coral}}')
    + sparkle(36, 10, 7, '{{yellow}}')
    + dot(31, 30, 2.6, '{{pink}}') + dot(42, 40, 2.6, '{{yellow}}') + dot(27, 44, 2.6, '{{white}}') + dot(46, 47, 2.4, '{{softPink}}'),

  // Fireworks: a big pink burst, a small yellow one, lavender sparks.
  newYear: (() => {
    const burst = (x, y, r0, r1, n, c, w) => Array.from({ length: n }, (_, i) => {
      const a = (i * 2 * Math.PI) / n
      return line(`M${(x + r0 * Math.cos(a)).toFixed(1)} ${(y + r0 * Math.sin(a)).toFixed(1)}L${(x + r1 * Math.cos(a)).toFixed(1)} ${(y + r1 * Math.sin(a)).toFixed(1)}`, c, w)
    }).join('')
    return `<g transform="translate(2.5 2.5)">${burst(30, 30, 7, 20, 8, '{{depth}}', 4.5)}</g>${burst(30, 30, 7, 20, 8, '{{pink}}', 4.5)}`
      + `<g transform="translate(2 2)">${burst(51, 48, 4, 12, 8, '{{depth}}', 3.5)}</g>${burst(51, 48, 4, 12, 8, '{{yellow}}', 3.5)}`
      + dot(30, 30, 3.5, '{{coral}}') + sparkle(56, 18, 6, '{{lavender}}') + sparkle(18, 56, 5, '{{mint}}')
  })(),

  // A coral pumpkin with a green stem and an ink face.
  halloween: lift(path('M36 22c-4-3-12-3-17 1-7 5-8 19-2 27 4 5 11 6 15 4 2 1 6 1 8 0 4 2 11 1 15-4 6-8 5-22-2-27-5-4-13-4-17-1z'), '{{coral}}')
    + line('M36 23v30M26 25c-3 8-3 20 1 27M46 25c3 8 3 20-1 27', '{{ink}}', 1.6).replace('stroke="{{ink}}"', 'stroke="{{ink}}" stroke-opacity="0.18"')
    + rect(33, 12, 6, 12, 3, '{{green}}')
    + path('M25 34l5-5 5 5z')('{{ink}}') + path('M37 34l5-5 5 5z')('{{ink}}')
    + line('M26 43l4 3 3-3 3 3 3-3 3 3 4-3', '{{ink}}', 2.4),

  // A lavender egg with a yellow zigzag band and pink spots, a mint egg behind.
  easter: lift(path('M50 26c6 0 10 10 10 19s-5 15-10 15-10-6-10-15 4-19 10-19z'), '{{mint}}')
    + lift(path('M30 10c10 0 18 17 18 30s-8 22-18 22-18-9-18-22 8-30 18-30z'), '{{lavender}}')
    + line('M13 38l6-5 6 5 5-5 6 5 6-5 5 5', '{{yellow}}', 4)
    + dot(24, 24, 2.6, '{{pink}}') + dot(34, 22, 2.2, '{{white}}') + dot(22, 50, 2.6, '{{white}}') + dot(36, 52, 2.6, '{{pink}}'),

  // A big pink heart, a small soft pink one, a sparkle.
  valentines: lift(path(heartD(32, 38, 20)), '{{pink}}')
    + lift(path(heartD(54, 20, 9)), '{{softPink}}', 2, 2)
    + path('M20 30c1-5 5-8 9-8')('none').replace('fill="none"', 'fill="none" stroke="{{white}}" stroke-width="3.2" stroke-linecap="round"')
    + sparkle(56, 52, 6, '{{yellow}}'),

  // A yellow sun with rays, ink sunglasses, a mint wave.
  summer: (() => {
    const rays = Array.from({ length: 8 }, (_, i) => `<rect x="33.5" y="4" width="5" height="10" rx="2.5" fill="{{coral}}" transform="rotate(${i * 45} 36 30)"/>`).join('')
    return rays + lift((f) => dot(36, 30, 15, f), '{{yellow}}')
      + rect(25, 26, 9, 6, 3, '{{ink}}') + rect(38, 26, 9, 6, 3, '{{ink}}') + rect(33, 27, 6, 2, 1, '{{ink}}')
      + line('M30 37c3 2 9 2 12 0', '{{ink}}', 2.2)
      + lift(path('M8 58c5-5 9-5 14 0s9 5 14 0 9-5 14 0 9 5 14 0v6H8z'), '{{mint}}', 2, 2)
  })(),

  // A calendar with bunting strung across it: a day off.
  bankHoliday: calendar('{{mint}}', '{{lavender}}')
    + line('M14 36c10 6 34 6 44 0', '{{ink}}', 1.6)
    + path('M18 38l4 9 4-8z')('{{pink}}') + path('M30 40l4 9 4-9z')('{{yellow}}') + path('M42 39l4 9 4-10z')('{{white}}'),

  // A party popper: a yellow cone, confetti flying.
  celebration: lift(soft('M14 60L26 26 46 46Z', 3), '{{yellow}}')
    + line('M20 44l8 8M23 35l12 12', '{{coral}}', 2.6)
    + rect(42, 10, 5, 9, 2, '{{pink}}', ' transform="rotate(30 44 14)"')
    + dot(56, 24, 3.2, '{{mint}}') + dot(36, 16, 2.6, '{{lavender}}')
    + line('M48 32c3-2 5 1 8-1s4-3 6-1', '{{lavender}}', 3)
    + rect(54, 40, 5, 9, 2, '{{coral}}', ' transform="rotate(-25 56 44)"')
    + sparkle(60, 56, 5, '{{pink}}'),

  // A rocket nose up with a flame, and a sparkle: something new.
  newFeature: `<g transform="rotate(35 36 36)">`
    + path('M28 50h16l-3 10-5-4-5 4z')('{{yellow}}')
    + lift(path('M28 32c-6 3-9 8-9 14l9-2zM44 32c6 3 9 8 9 14l-9-2z'), '{{coral}}', 2, 2)
    + lift(path('M36 6c9 7 11 20 9 44H27C25 26 27 13 36 6z'), '{{softPink}}', 2.5, 2.5)
    + dot(36, 26, 6.5, '{{ink}}') + dot(36, 26, 4, '{{lavender}}')
    + `</g>`
    + sparkle(14, 16, 7, '{{yellow}}') + sparkle(58, 56, 5, '{{mint}}'),

  // An hourglass: coral caps, lavender glass, yellow sand.
  comingSoon: lift(path('M23 16h26c0 12-8 15-8 20s8 8 8 20H23c0-12 8-15 8-20s-8-8-8-20z'), '{{lavender}}')
    + path('M27 22h18c-1 5-6 8-9 9-3-1-8-4-9-9z')('{{yellow}}')
    + path('M26 54h20c0-5-5-9-10-10-5 1-10 5-10 10z')('{{yellow}}')
    + lift((f) => rect(18, 10, 36, 7, 3.5, f), '{{coral}}', 2, 2)
    + lift((f) => rect(18, 55, 36, 7, 3.5, f), '{{coral}}', 2, 2),

  // A calendar with a yellow star on the day.
  event: calendar('{{yellow}}', '{{pink}}')
    + path(starD(36, 45, 9))('{{coral}}').replace('/>', ' stroke="{{coral}}" stroke-width="2" stroke-linejoin="round"/>'),

  // A screen with a play mark and a speech bubble: a talk to watch.
  webinar: lift(path('M12 14h40a5 5 0 0 1 5 5v24a5 5 0 0 1-5 5H12a5 5 0 0 1-5-5V19a5 5 0 0 1 5-5z'), '{{lavender}}')
    + rect(26, 48, 12, 8, 2, '{{lavender}}') + rect(20, 56, 24, 5, 2.5, '{{depth}}')
    + path('M28 24l12 7-12 7z')('{{white}}').replace('/>', ' stroke="{{white}}" stroke-width="2.5" stroke-linejoin="round"/>')
    + lift(path('M48 34h14a4 4 0 0 1 4 4v8a4 4 0 0 1-4 4h-6l-5 5v-5h-3a4 4 0 0 1-4-4v-8a4 4 0 0 1 4-4z'), '{{pink}}', 2, 2)
    + dot(51, 42, 1.6, '{{white}}') + dot(55, 42, 1.6, '{{white}}') + dot(59, 42, 1.6, '{{white}}'),

  // A yellow light bulb, a lavender base, coral rays: a tip.
  tip: line('M36 5v5M16 13l4 4M56 13l-4 4M9 31h5M58 31h5', '{{coral}}', 3.4)
    + lift(path('M36 12c11 0 19 8 19 18 0 7-4 11-7 15-2 2-2 5-2 7H26c0-2 0-5-2-7-3-4-7-8-7-15 0-10 8-18 19-18z'), '{{yellow}}')
    + path('M27 26c1-4 4-7 8-8')('none').replace('fill="none"', 'fill="none" stroke="{{white}}" stroke-width="3.2" stroke-linecap="round"')
    + lift((f) => rect(26, 52, 20, 10, 4, f), '{{lavender}}', 2, 2)
    + rect(29, 56, 14, 2, 1, '{{ink}}'),

  // A mint banknote with two yellow coins in front: pay day.
  payday: lift((f) => rect(8, 16, 46, 28, 5, f), '{{mint}}')
    + dot(31, 30, 7, '{{white}}') + dot(31, 30, 3, '{{green}}')
    + rect(13, 21, 6, 3, 1.5, '{{white}}') + rect(43, 36, 6, 3, 1.5, '{{white}}')
    + lift((f) => `<ellipse cx="48" cy="56" rx="14" ry="6" fill="${f}"/>`, '{{yellow}}', 2, 2)
    + `<ellipse cx="48" cy="50" rx="14" ry="6" fill="{{coral}}"/>`
    + `<ellipse cx="48" cy="45" rx="14" ry="6" fill="{{yellow}}"/>`
    + `<ellipse cx="48" cy="45" rx="8" ry="3" fill="{{coral}}" fill-opacity="0.45"/>`,

  // A coral wallet with a lavender card peeking out, a mint plus: top up.
  wallet: lift((f) => rect(14, 12, 30, 22, 4, f, ' transform="rotate(-10 29 23)"'), '{{lavender}}', 2, 2)
    + lift(path('M12 24h40a6 6 0 0 1 6 6v20a6 6 0 0 1-6 6H12a6 6 0 0 1-6-6V30a6 6 0 0 1 6-6z'), '{{coral}}')
    + path('M44 34h14v12H44a6 6 0 0 1 0-12z')('{{pink}}') + dot(45, 40, 2.6, '{{yellow}}')
    + lift((f) => dot(56, 18, 9, f), '{{mint}}', 2, 2)
    + rect(55, 13, 2.4, 10, 1.2, '{{white}}') + rect(51, 16.8, 10, 2.4, 1.2, '{{white}}'),

  // A mint shield with a white tick.
  safety: lift(path('M36 8l22 8v16c0 14-9 24-22 30C23 56 14 46 14 32V16z'), '{{mint}}')
    + line('M26 35l7 7 13-14', '{{white}}', 4.5)
    + sparkle(58, 54, 5, '{{yellow}}'),

  // A yellow document with a coral corner, pink lines and a lavender seal.
  compliance: lift(path('M20 8h22l10 10v36a6 6 0 0 1-6 6H20a6 6 0 0 1-6-6V14a6 6 0 0 1 6-6z'), '{{yellow}}')
    + path('M42 8v6a4 4 0 0 0 4 4h6z')('{{coral}}')
    + rect(20, 24, 20, 4, 2, '{{pink}}') + rect(20, 32, 24, 4, 2, '{{pink}}') + rect(20, 40, 14, 4, 2, '{{pink}}')
    + lift((f) => dot(50, 50, 10, f), '{{lavender}}', 2, 2)
    + line('M45.5 50l3 3 6-6', '{{white}}', 3),

  // A pink gift box with a yellow ribbon and bow.
  referral: lift((f) => rect(14, 32, 44, 28, 4, f), '{{pink}}')
    + lift((f) => rect(10, 24, 52, 11, 4, f), '{{softPink}}', 2, 2)
    + rect(32, 24, 8, 36, 2, '{{yellow}}')
    + path('M36 24c-6-10-16-12-17-6-1 5 9 6 17 6zM36 24c6-10 16-12 17-6 1 5-9 6-17 6z')('{{yellow}}')
    + dot(36, 24, 3.4, '{{coral}}'),

  // Three bars climbing, a coral arrow up: rates.
  rate: lift((f) => rect(10, 40, 12, 20, 3, f), '{{lavender}}', 2, 2)
    + lift((f) => rect(28, 30, 12, 30, 3, f), '{{mint}}', 2, 2)
    + lift((f) => rect(46, 20, 12, 40, 3, f), '{{pink}}', 2, 2)
    + line('M10 30L24 20l10 6L52 10', '{{coral}}', 4) + line('M44 9h9v9', '{{coral}}', 4),

  // A coral pin with a white centre over a lavender spot.
  location: `<ellipse cx="36" cy="60" rx="14" ry="4.5" fill="{{lavender}}"/>`
    + lift(path('M36 8c11 0 19 8 19 19 0 13-13 23-19 31-6-8-19-18-19-31 0-11 8-19 19-19z'), '{{coral}}')
    + dot(36, 27, 7, '{{white}}') + sparkle(58, 14, 5, '{{yellow}}'),

  // Two speech bubbles, yellow behind, pink in front.
  support: lift(path('M8 12h32a4 4 0 0 1 4 4v18a4 4 0 0 1-4 4H20l-9 8 1-8H8a4 4 0 0 1-4-4V16a4 4 0 0 1 4-4z'), '{{yellow}}', 2.5, 2.5)
    + line('M12 26c3-6 5-6 6 0s4 4 6-2 4-4 5 1', '{{ink}}', 2.4)
    + lift(path('M30 28h32a4 4 0 0 1 4 4v18a4 4 0 0 1-4 4h-4l1 8-9-8H30a4 4 0 0 1-4-4V32a4 4 0 0 1 4-4z'), '{{pink}}', 2.5, 2.5)
    + line('M35 42c4-5 7 4 11 0s7-4 11 1', '{{white}}', 2.6),

  // A big yellow star, a pink sparkle: reviews.
  star: lift(soft(starD(34, 38, 24, 11), 5), '{{yellow}}')
    + path('M24 30l5-1')('none').replace('fill="none"', 'fill="none" stroke="{{white}}" stroke-width="3" stroke-linecap="round"')
    + sparkle(58, 14, 7, '{{pink}}') + sparkle(60, 58, 4.5, '{{lavender}}'),

  // Two gears turning: maintenance.
  maintenance: `<g transform="translate(3 3)">${gear(28, 34, 15, '{{depth}}')}</g>${gear(28, 34, 15, '{{lavender}}')}`
    + dot(28, 34, 6, '{{white}}')
    + `<g transform="translate(2 2)">${gear(52, 20, 9, '{{depth}}')}</g>${gear(52, 20, 9, '{{yellow}}')}`
    + dot(52, 20, 3.4, '{{white}}') + sparkle(56, 52, 5, '{{pink}}'),

  // A lavender envelope sealed with a pink heart: thank you.
  thanks: lift(path('M12 20h48a5 5 0 0 1 5 5v28a5 5 0 0 1-5 5H12a5 5 0 0 1-5-5V25a5 5 0 0 1 5-5z'), '{{lavender}}')
    + line('M9 23l27 19 27-19', '{{white}}', 3)
    + lift(path(heartD(36, 42, 10)), '{{pink}}', 2, 2)
    + sparkle(60, 12, 6, '{{yellow}}'),
}

export const ANNOUNCEMENT_ICON_XML = Object.fromEntries(Object.entries(ICONS).map(
  ([k, body]) => [k, `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 72 72">${body}</svg>`],
))

// Fill the slots from a tone set (shared/pastelTones.js PASTEL_TONES.light/dark).
// An unknown slot draws nothing rather than a wrong colour.
export function fillIcon(xml, tones) {
  return String(xml || '').replace(/\{\{(\w+)\}\}/g, (_, slot) => tones?.[slot] ?? 'none')
}
